import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { DealStage, DealStageInsert } from "@/types/deal";

export function useDealStages(workspaceId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["deal-stages", workspaceId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("deal_stages")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("order_index");
      if (error) throw error;
      return data as DealStage[];
    },
    enabled: !!workspaceId,
    refetchOnWindowFocus: false,
  });

  const updateStage = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DealStage> & { id: string }) => {
      const { error } = await supabase.from("deal_stages").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const createStage = useMutation({
    mutationFn: async (stage: DealStageInsert) => {
      const { error } = await supabase.from("deal_stages").insert(stage);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteStage = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("deal_stages").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  return { ...query, stages: query.data ?? [], updateStage, createStage, deleteStage };
}
